import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, LinearProgress, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useAuth } from '../../providers/AuthProvider';
import { attendanceService, PhotoStats } from '../../services/attendanceService';
import { clearPhotoCache } from '../../components/StudentPhoto/StudentPhoto';

// Lots de ~5 Mo : la prod limite les requêtes à 10 Mo (client_max_body_size nginx)
const BATCH_BYTES = 5 * 1024 * 1024;

function batches(files: File[]): File[][] {
  const result: File[][] = [];
  let current: File[] = [];
  let size = 0;
  for (const file of files) {
    if (current.length && size + file.size > BATCH_BYTES) {
      result.push(current);
      current = [];
      size = 0;
    }
    current.push(file);
    size += file.size;
  }
  if (current.length) result.push(current);
  return result;
}

// Photos du trombinoscope (appel en mode trombi) : dépôt du dossier de photos de Mystic, fichiers
// <numéro étudiant>.jpg. Le serveur ne garde que les photos des étudiant·es inscrit·es ici.
const StudentPhotosPanel: React.FC = () => {
  const { getValidToken } = useAuth();
  const [stats, setStats] = useState<PhotoStats | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [result, setResult] = useState<{ stored: number; ignored: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    try {
      const token = await getValidToken();
      if (token) setStats(await attendanceService.getPhotoStats(token));
    } catch (e: any) {
      setError(e.message || 'Erreur lors du chargement des photos');
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (fileList: FileList | null) => {
    const files = Array.from(fileList || []).filter(f => /\.(jpe?g|png)$/i.test(f.name));
    if (!files.length) {
      setError('Aucun fichier .jpg ou .png dans la sélection');
      return;
    }
    setError(null);
    setResult(null);
    let stored = 0;
    let ignored = 0;
    try {
      const groups = batches(files);
      for (let i = 0; i < groups.length; i++) {
        setProgress(Math.round((i / groups.length) * 100));
        const token = await getValidToken();
        if (!token) throw new Error('Session expirée, reconnectez-vous.');
        const response = await attendanceService.uploadPhotos(groups[i], token);
        stored += response.stored;
        ignored += response.ignored.length;
      }
      setResult({ stored, ignored });
      clearPhotoCache();
      await loadStats();
    } catch (e: any) {
      setError(e.message || "Erreur lors de l'envoi des photos");
    } finally {
      setProgress(null);
    }
  };

  return (
    <Accordion sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">
          Photos du trombinoscope{stats ? ` — ${stats.with_photo}/${stats.students} étudiant·es` : ''}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sélectionnez le dossier de photos de Mystic (fichiers nommés par numéro étudiant, ex. 22100070.jpg).
          Seules les photos des étudiant·es inscrit·es ici, avec un numéro étudiant renseigné, sont gardées ; les autres
          sont ignorées. Un nouvel envoi remplace les photos existantes. Photos visibles uniquement des enseignant·es et admins.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button variant="outlined" component="label" startIcon={<PhotoLibraryIcon />} disabled={progress !== null}>
            Choisir le dossier
            {/* webkitdirectory : sélection d'un dossier entier (Firefox, Chrome, Edge) */}
            <input type="file" hidden multiple accept=".jpg,.jpeg,.png"
              {...{ webkitdirectory: '', directory: '' } as any}
              onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
          </Button>
          <Button variant="outlined" component="label" disabled={progress !== null}>
            Choisir des fichiers
            <input type="file" hidden multiple accept=".jpg,.jpeg,.png" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} />
          </Button>
        </Box>
        {progress !== null && <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {result.stored} photo(s) enregistrée(s), {result.ignored} ignorée(s) (autres promos ou fichiers invalides).
          </Alert>
        )}
        {stats && stats.without_student_number > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {stats.without_student_number} étudiant·e(s) sans numéro étudiant : leur photo ne peut pas être rattachée
            (renseigner le numéro via l'import des numéros étudiants).
          </Alert>
        )}
        {stats && stats.missing.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            Sans photo : {stats.missing.map(s => `${s.lastname || ''} ${s.firstname || ''} (${s.td_group || '—'})`).join(', ')}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default StudentPhotosPanel;
